// Route Service - Manages community-drawn facility routes
// Handles saving, loading, and voting on caliche road routes

export interface RouteWaypoint {
  latitude: number;
  longitude: number;
  label?: string;
  type?: 'marker' | 'turn' | 'landmark' | 'warning' | 'gate' | 'start' | 'destination';
  sequenceOrder?: number;
}

export interface RouteData {
  encodedPolyline?: string;
  polyline?: Array<{ lat: number; lng: number }>;
  startLat?: number;
  startLng?: number;
  endLat?: number;
  endLng?: number;
}

export interface FacilityRoute {
  id: string;
  facilityId: string;
  facilityType: 'well' | 'swd' | 'rig' | 'other';
  userId: string;
  userName: string;
  routeData: RouteData;
  description?: string;
  startDescription?: string;
  upvotes: number;
  downvotes: number;
  score: number;
  waypoints?: RouteWaypoint[];
  waypointCount: number;
  userVote?: number; // 1, -1, or 0
  createdAt: string;
  updatedAt: string;
}

export interface RoutesResponse {
  success: boolean;
  routes?: FacilityRoute[];
  error?: string;
}

export interface RouteGoogleMapsNavigationPath {
  destination: { lat: number; lng: number };
  waypoints: Array<{ lat: number; lng: number }>;
  hasRouteCoordinates: boolean;
}

const API_BASE = '/api/routes';

const parseRouteData = (routeData: unknown): RouteData => {
  if (!routeData) return {};
  if (typeof routeData === 'string') {
    try {
      const parsed = JSON.parse(routeData) as unknown;
      // Handle double-encoded JSON strings from older clients
      if (typeof parsed === 'string') {
        try {
          return normalizeRouteData(JSON.parse(parsed));
        } catch {
          return {};
        }
      }
      return normalizeRouteData(parsed);
    } catch {
      return {};
    }
  }
  if (typeof routeData === 'object') return normalizeRouteData(routeData);
  return {};
};

/** Normalize route_data keys + derive missing start/end from polyline when possible */
const normalizeRouteData = (raw: unknown): RouteData => {
  if (!raw || typeof raw !== 'object') return {};
  const r = raw as Record<string, unknown>;
  const polyRaw = r.polyline ?? r.path ?? r.points;
  let polyline: Array<{ lat: number; lng: number }> | undefined;
  if (Array.isArray(polyRaw)) {
    polyline = polyRaw
      .map((p) => {
        if (!p || typeof p !== 'object') return null;
        const rec = p as Record<string, unknown>;
        const lat = Number(rec.lat ?? rec.latitude);
        const lng = Number(rec.lng ?? rec.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return { lat, lng };
      })
      .filter((p): p is { lat: number; lng: number } => p !== null);
  }
  const startLat = Number(r.startLat ?? r.start_lat ?? polyline?.[0]?.lat);
  const startLng = Number(r.startLng ?? r.start_lng ?? polyline?.[0]?.lng);
  const endLat = Number(r.endLat ?? r.end_lat ?? polyline?.[polyline.length - 1]?.lat);
  const endLng = Number(r.endLng ?? r.end_lng ?? polyline?.[polyline.length - 1]?.lng);
  const out: RouteData = {};
  if (polyline && polyline.length > 0) out.polyline = polyline;
  if (typeof r.encodedPolyline === 'string') out.encodedPolyline = r.encodedPolyline;
  if (Number.isFinite(startLat)) out.startLat = startLat;
  if (Number.isFinite(startLng)) out.startLng = startLng;
  if (Number.isFinite(endLat)) out.endLat = endLat;
  if (Number.isFinite(endLng)) out.endLng = endLng;
  return out;
};

/** Normalize waypoints from API (snake_case columns or camelCase client) */
export const normalizeFacilityWaypoints = (waypoints: unknown): RouteWaypoint[] => {
  if (!Array.isArray(waypoints)) return [];
  return waypoints
    .map((wp, index): RouteWaypoint | null => {
      if (!wp || typeof wp !== 'object') return null;
      const rec = wp as Record<string, unknown>;
      const latitude = Number(rec.latitude ?? rec.lat);
      const longitude = Number(rec.longitude ?? rec.lng);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
      if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;
      const rawSeq = Number(rec.sequence_order ?? rec.sequenceOrder ?? index);
      return {
        latitude,
        longitude,
        label: String(rec.label || rec.name || '').trim() || undefined,
        type: normalizeRouteType(rec.waypoint_type ?? rec.type),
        sequenceOrder: Number.isFinite(rawSeq) ? rawSeq : index,
      };
    })
    .filter((wp): wp is RouteWaypoint => wp !== null)
    .sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0));
};

const isValidRouteCoord = (point: { lat?: unknown; lng?: unknown }): point is { lat: number; lng: number } => {
  const lat = Number(point.lat);
  const lng = Number(point.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
};

const isSameRouteCoord = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => (
  Math.abs(a.lat - b.lat) < 0.000001 && Math.abs(a.lng - b.lng) < 0.000001
);

const sampleRoutePoints = (points: Array<{ lat: number; lng: number }>, maxWaypoints: number): Array<{ lat: number; lng: number }> => {
  if (points.length <= maxWaypoints) return points;
  const sampled: Array<{ lat: number; lng: number }> = [];
  for (let i = 1; i <= maxWaypoints; i += 1) {
    const index = Math.round((i * (points.length - 1)) / (maxWaypoints + 1));
    sampled.push(points[index]);
  }
  return sampled;
};

const normalizeRouteType = (type: unknown): RouteWaypoint['type'] => {
  const normalized = String(type || '').trim().toLowerCase().replace(/[\s_-]+/g, '_');
  if (normalized === 'start') return 'start';
  if (normalized === 'destination' || normalized === 'finish' || normalized === 'end') return 'destination';
  if (normalized === 'turn') return 'turn';
  if (normalized === 'warning') return 'warning';
  if (normalized === 'gate' || normalized === 'cattle_guard') return 'gate';
  if (normalized === 'landmark') return 'landmark';
  return 'marker';
};

const normalizeLandmarkWaypoints = (landmarks: unknown): RouteWaypoint[] => {
  if (!Array.isArray(landmarks)) return [];

  return landmarks
    .map((landmark, index): RouteWaypoint | null => {
      if (!landmark || typeof landmark !== 'object') return null;
      const record = landmark as Record<string, unknown>;
      const latitude = Number(record.latitude ?? record.lat);
      const longitude = Number(record.longitude ?? record.lng);
      const point = { lat: latitude, lng: longitude };
      if (!isValidRouteCoord(point)) return null;
      const rawSequence = Number(record.sequence_order ?? record.sequenceOrder ?? index);
      const sequenceOrder = Number.isFinite(rawSequence) ? rawSequence : index;
      const label = String(record.name || record.description || record.landmark_type || record.type || '').trim();
      return {
        latitude,
        longitude,
        label: label || undefined,
        type: normalizeRouteType(record.landmark_type ?? record.type),
        sequenceOrder,
      };
    })
    .filter((point): point is RouteWaypoint => point !== null)
    .sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0));
};

const routeWaypointCoords = (route: FacilityRoute): Array<{ lat: number; lng: number }> => (
  normalizeFacilityWaypoints(route.waypoints)
    .map((wp) => ({ lat: wp.latitude, lng: wp.longitude }))
    .filter(isValidRouteCoord)
);

const routePolylineCoords = (route: FacilityRoute): Array<{ lat: number; lng: number }> => {
  const data = normalizeRouteData(route.routeData || {});
  return (data.polyline || [])
    .map((point) => ({ lat: Number(point.lat), lng: Number(point.lng) }))
    .filter(isValidRouteCoord);
};

export function getRouteGoogleMapsNavigationPath(
  route: FacilityRoute,
  fallbackDestination?: { lat: number; lng: number } | null,
  maxWaypoints = 9,
): RouteGoogleMapsNavigationPath | null {
  const data = normalizeRouteData(route.routeData || {});
  const explicitWaypoints = routeWaypointCoords(route);
  const polyline = routePolylineCoords(route);
  const routeEnd = {
    lat: Number(data.endLat),
    lng: Number(data.endLng),
  };
  const hasRouteEnd = isValidRouteCoord(routeEnd);
  const fallback = fallbackDestination && isValidRouteCoord(fallbackDestination) ? fallbackDestination : null;

  // Prefer full drawn polyline when available (finger-drawn caliche road)
  if (polyline.length >= 2) {
    const destination = hasRouteEnd ? routeEnd : polyline[polyline.length - 1];
    const routeWaypoints = polyline
      .slice(0, -1)
      .filter((point) => !isSameRouteCoord(point, destination));
    // Also merge any explicit markers not already on the polyline
    const extras = explicitWaypoints.filter(
      (wp) => !polyline.some((p) => isSameRouteCoord(p, wp)) && !isSameRouteCoord(wp, destination),
    );
    return {
      destination,
      waypoints: sampleRoutePoints([...routeWaypoints, ...extras], maxWaypoints),
      hasRouteCoordinates: true,
    };
  }

  if (hasRouteEnd) {
    const sourcePoints = explicitWaypoints.length > 0 ? explicitWaypoints : polyline;
    const routeWaypoints = sourcePoints.filter((point) => !isSameRouteCoord(point, routeEnd));
    return {
      destination: routeEnd,
      waypoints: sampleRoutePoints(routeWaypoints, maxWaypoints),
      hasRouteCoordinates: true,
    };
  }

  if (explicitWaypoints.length > 0) {
    if (fallback) {
      return {
        destination: fallback,
        waypoints: sampleRoutePoints(explicitWaypoints, maxWaypoints),
        hasRouteCoordinates: true,
      };
    }
    const destination = explicitWaypoints[explicitWaypoints.length - 1];
    return {
      destination,
      waypoints: sampleRoutePoints(explicitWaypoints.slice(0, -1), maxWaypoints),
      hasRouteCoordinates: true,
    };
  }

  if (polyline.length > 0) {
    const destination = polyline[polyline.length - 1];
    return {
      destination,
      waypoints: sampleRoutePoints(polyline.slice(0, -1), maxWaypoints),
      hasRouteCoordinates: true,
    };
  }

  if (fallback) {
    return {
      destination: fallback,
      waypoints: [],
      hasRouteCoordinates: false,
    };
  }

  return null;
}

export function buildGoogleMapsRouteUrl(
  destination: { lat: number; lng: number },
  waypoints: Array<{ lat: number; lng: number }> = [],
): string {
  const params = new URLSearchParams({
    api: '1',
    destination: `${destination.lat},${destination.lng}`,
    travelmode: 'driving',
    dir_action: 'navigate',
  });
  if (waypoints.length > 0) {
    params.set('waypoints', waypoints.map(point => `${point.lat},${point.lng}`).join('|'));
  }
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

export function getRouteNavigationWaypoints(route: FacilityRoute, maxWaypoints = 23): Array<{ lat: number; lng: number }> {
  const explicitWaypoints = routeWaypointCoords(route);

  if (explicitWaypoints.length > 0) {
    const routeEnd = {
      lat: Number(route.routeData?.endLat),
      lng: Number(route.routeData?.endLng),
    };
    const viaPoints = isValidRouteCoord(routeEnd)
      ? explicitWaypoints.filter((point) => !isSameRouteCoord(point, routeEnd))
      : explicitWaypoints;
    return sampleRoutePoints(viaPoints, maxWaypoints);
  }

  const polyline = routePolylineCoords(route);

  if (polyline.length <= 2) return [];
  return sampleRoutePoints(polyline.slice(1, -1), maxWaypoints);
}

export const RouteService = {
  /**
   * Get all routes for a facility
   */
  async getRoutes(facilityId: string, facilityType: string = 'well', userId?: string): Promise<RoutesResponse> {
    try {
      const params = new URLSearchParams({ facilityId, facilityType });
      if (userId) params.append('userId', userId);
      
      const response = await fetch(`${API_BASE}?${params}`);
      const data = await response.json();
      
      if (data.success) {
        // Transform snake_case to camelCase
        const routes = (data.routes || []).map((r: Record<string, unknown>) => {
          const routeData = parseRouteData(r.route_data ?? r.routeData);
          let waypoints = normalizeFacilityWaypoints(r.waypoints);
          // Drawn routes often have polyline only (no marker waypoints).
          // Seed nav waypoints from the polyline so Google Maps can follow the road.
          if (waypoints.length === 0 && routeData.polyline && routeData.polyline.length >= 2) {
            const sampled = sampleRoutePoints(routeData.polyline, 12);
            waypoints = sampled.map((p, i) => ({
              latitude: p.lat,
              longitude: p.lng,
              type: i === 0 ? 'start' : i === sampled.length - 1 ? 'destination' : 'marker',
              sequenceOrder: i,
            }));
          }
          return {
            id: r.id,
            facilityId: r.facility_id,
            facilityType: r.facility_type,
            userId: r.user_id,
            userName: r.user_name,
            routeData,
            description: r.description,
            startDescription: r.start_description,
            upvotes: r.upvotes || 0,
            downvotes: r.downvotes || 0,
            score: r.score || 0,
            waypoints,
            waypointCount: waypoints.length || r.waypoint_count || 0,
            userVote: r.user_vote,
            createdAt: r.created_at,
            updatedAt: r.updated_at,
          } as FacilityRoute;
        });
        return { success: true, routes };
      }
      console.error('Failed to fetch routes:', data.error);
      return { success: false, error: data.error || 'Failed to fetch routes' };
    } catch (error) {
      console.error('Error fetching routes:', error);
      return { success: false, error: 'Network error' };
    }
  },

  /**
   * Get routes for a facility from both legacy facility_routes and newer
   * landmark_routes. The well details modal uses this so saved Caliche routes
   * always appear regardless of which route workflow created them.
   */
  async getRoutesForFacilityIdentifiers(facilityIds: string[], facilityType: string = 'well', userId?: string): Promise<RoutesResponse> {
    const ids = Array.from(new Set(facilityIds.map((id) => String(id || '').trim()).filter(Boolean)));
    if (ids.length === 0) return { success: true, routes: [] };

    const routeMap = new Map<string, FacilityRoute>();

    const addRoute = (route: FacilityRoute | null | undefined) => {
      if (!route?.id) return;
      if (!routeMap.has(route.id)) routeMap.set(route.id, route);
    };

    await Promise.all(ids.map(async (facilityId) => {
      const legacy = await RouteService.getRoutes(facilityId, facilityType, userId);
      if (legacy.success) {
        (legacy.routes || []).forEach(addRoute);
      }

      try {
        const response = await fetch(`/api/landmark-routes/facility/${encodeURIComponent(facilityId)}`);
        if (!response.ok) return;
        const data = await response.json();
        if (!data.success || !Array.isArray(data.routes)) return;
        data.routes.forEach((route: Record<string, unknown>) => {
          const routeId = String(route.route_id || route.id || '');
          if (!routeId) return;
          const landmarkWaypoints = normalizeLandmarkWaypoints(route.landmarks);
          const start = landmarkWaypoints[0];
          const end = landmarkWaypoints[landmarkWaypoints.length - 1];
          addRoute({
            id: routeId,
            facilityId: String(route.facility_id || facilityId),
            facilityType: 'well',
            userId: String(route.created_by_user_id || ''),
            userName: String(route.created_by_username || 'Anonymous'),
            routeData: landmarkWaypoints.length > 0
              ? {
                  polyline: landmarkWaypoints.map((point) => ({ lat: point.latitude, lng: point.longitude })),
                  startLat: start?.latitude,
                  startLng: start?.longitude,
                  endLat: end?.latitude,
                  endLng: end?.longitude,
                }
              : {},
            description: String(route.description || route.name || ''),
            startDescription: '',
            upvotes: Number(route.upvotes || 0),
            downvotes: Number(route.downvotes || 0),
            score: Number(route.upvotes || 0) - Number(route.downvotes || 0),
            waypoints: landmarkWaypoints,
            waypointCount: landmarkWaypoints.length || Number(route.landmark_count || 0),
            userVote: 0,
            createdAt: String(route.created_at || ''),
            updatedAt: String(route.updated_at || route.created_at || ''),
          });
        });
      } catch (error) {
        console.error('Error fetching landmark routes:', error);
      }
    }));

    return { success: true, routes: Array.from(routeMap.values()) };
  },

  /**
   * Submit a new route
   */
  async submitRoute(
    facilityId: string,
    facilityType: string,
    userId: string,
    userName: string,
    routeData: RouteData,
    description?: string,
    startDescription?: string,
    waypoints?: RouteWaypoint[]
  ): Promise<{ success: boolean; routeId?: string; error?: string }> {
    try {
      const response = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          facilityId,
          facilityType,
          userId,
          userName,
          routeData,
          description,
          startDescription,
          waypoints
        })
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error submitting route:', error);
      return { success: false, error: 'Network error' };
    }
  },

  /**
   * Vote on a route (upvote/downvote)
   */
  async vote(routeId: string, userId: string, vote: 1 | -1 | 0): Promise<{
    success: boolean;
    upvotes?: number;
    downvotes?: number;
    score?: number;
    error?: string;
  }> {
    try {
      const response = await fetch(`${API_BASE}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ routeId, userId, vote })
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error voting:', error);
      return { success: false, error: 'Network error' };
    }
  },

  /**
   * Delete a route (only own routes)
   */
  async deleteRoute(routeId: string, userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const params = new URLSearchParams({ routeId, userId });
      const response = await fetch(`${API_BASE}?${params}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting route:', error);
      return { success: false, error: 'Network error' };
    }
  },

  /**
   * Convert Google Maps polyline to our format
   */
  polylineToRouteData(polyline: google.maps.Polyline): RouteData {
    const path = polyline.getPath();
    const points: Array<{ lat: number; lng: number }> = [];
    
    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i);
      points.push({ lat: point.lat(), lng: point.lng() });
    }
    
    const startPoint = points[0];
    const endPoint = points[points.length - 1];
    
    return {
      polyline: points,
      startLat: startPoint?.lat,
      startLng: startPoint?.lng,
      endLat: endPoint?.lat,
      endLng: endPoint?.lng
    };
  },

  /**
   * Draw a route on Google Maps
   */
  drawRouteOnMap(map: google.maps.Map, routeData: RouteData, options?: {
    strokeColor?: string;
    strokeWeight?: number;
    strokeOpacity?: number;
    clickable?: boolean;
  }): google.maps.Polyline | null {
    if (!routeData.polyline || routeData.polyline.length === 0) {
      return null;
    }
    
    const polyline = new google.maps.Polyline({
      path: routeData.polyline,
      strokeColor: options?.strokeColor || '#00f0ff',
      strokeWeight: options?.strokeWeight || 4,
      strokeOpacity: options?.strokeOpacity || 0.8,
      clickable: options?.clickable ?? true,
      map
    });
    
    return polyline;
  },

  /**
   * Draw waypoints on map
   */
  drawWaypointsOnMap(map: google.maps.Map, waypoints: RouteWaypoint[]): google.maps.Marker[] {
    const waypointIcons: Record<string, string> = {
      marker: '📍',
      turn: '↪️',
      landmark: '🏭',
      warning: '⚠️',
      gate: '🚧',
      start: '▶️',
      destination: '🏁'
    };
    
    return waypoints.map((wp, index) => {
      return new google.maps.Marker({
        position: { lat: wp.latitude, lng: wp.longitude },
        map,
        title: wp.label || `Waypoint ${index + 1}`,
        label: {
          text: waypointIcons[wp.type || 'marker'],
          fontSize: '20px'
        }
      });
    });
  }
};

export default RouteService;
